<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Badge, Button, EmptyState, PageHeader, Section, SegmentedControl, Spinner, Table, Toolbar } from '@drama/ui'
import { Plus } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import { fetchModelCapabilityCatalog } from '../../api/model-capabilities.js'
import { MODEL_CAPABILITIES } from './model-capability.js'
import ModelProviderModal from './provider/ModelProviderModal.vue'

const { t } = useI18n()
const router = useRouter()
const catalog = ref(null)
const loading = ref(true)
const error = ref('')
const enabledFilter = ref('')
const modalOpen = ref(false)

const filterOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.llm.providers.filters.all') },
  { value: 'enabled', label: t('visualGameAdmin.llm.providers.filters.enabledOnly') },
  { value: 'disabled', label: t('visualGameAdmin.llm.providers.filters.disabledOnly') },
])
const columns = computed(() => [
  { key: 'provider', label: t('visualGameAdmin.llm.providers.table.name'), width: '240px' },
  { key: 'capabilities', label: t('visualGameAdmin.llm.providers.table.capabilities'), width: '360px' },
  { key: 'models', label: t('visualGameAdmin.llm.providers.table.models'), width: '100px', align: 'right', mono: true },
  { key: 'enabled', label: t('visualGameAdmin.llm.providers.table.enabled'), width: '100px' },
  { key: 'actions', label: t('visualGameAdmin.llm.providers.table.actions'), width: '100px', align: 'right' },
])
const providerRows = computed(() => catalog.value.providers.map(provider => {
  const capabilities = providerCapabilities(catalog.value, provider.id)
  return {
    ...provider,
    capabilities,
    model_count: capabilities.reduce((total, capability) => total + modelCount(catalog.value, capability), 0),
  }
}))
const visibleProviders = computed(() => {
  if (enabledFilter.value === '') return providerRows.value
  const enabled = enabledFilter.value === 'enabled'
  return providerRows.value.filter(provider => (provider.enabled === 1) === enabled)
})

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    catalog.value = await fetchModelCapabilityCatalog()
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function openProvider(code) {
  router.push({ name: 'llm-provider-detail', params: { name: code } })
}

function providerCapabilities(source, providerId) {
  return MODEL_CAPABILITIES
    .flatMap(capability => source[capability].provider_capabilities)
    .filter(capability => capability.provider_id === providerId)
}

function modelCount(source, providerCapability) {
  return source[providerCapability.capability].models
    .filter(model => model.provider_capability_id === providerCapability.id)
    .length
}
</script>

<template>
  <div class="providers vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.providers.title')">
      <template #actions>
        <Button variant="primary" @click="modalOpen = true">
          <Plus :size="16" />
          {{ t('visualGameAdmin.llm.providers.addProvider') }}
        </Button>
      </template>
    </PageHeader>

    <Section :padded="!catalog || catalog.providers.length === 0">
      <template #actions>
        <Toolbar v-if="catalog && catalog.providers.length > 0" compact>
          <div class="drama-toolbar-field">
            <label>{{ t('visualGameAdmin.llm.providers.filters.enabled') }}</label>
            <SegmentedControl v-model="enabledFilter" :options="filterOptions" size="sm" />
          </div>
        </Toolbar>
      </template>

      <div v-if="loading" class="vga-state">
        <Spinner :label="t('visualGameAdmin.common.loading')" />
      </div>
      <div v-else-if="error" class="vga-state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <EmptyState v-else-if="catalog.providers.length === 0" :title="t('visualGameAdmin.llm.providers.empty.title')">
        <template #action>
          <Button variant="primary" @click="modalOpen = true">{{ t('visualGameAdmin.llm.providers.addProvider') }}</Button>
        </template>
      </EmptyState>
      <Table v-else :columns="columns" :rows="visibleProviders" row-key="code">
        <template #provider="{ row }">
          <div class="providers__identity">
            <strong>{{ row.display_name }}</strong>
            <code>{{ row.code }}</code>
          </div>
        </template>
        <template #capabilities="{ row }">
          <div class="providers__capabilities">
            <Badge
              v-for="capability in row.capabilities"
              :key="capability.id"
              :variant="capability.enabled ? 'info' : 'neutral'"
            >
              {{ t(`visualGameAdmin.llm.capabilities.types.${capability.capability}`) }} · {{ capability.code }}
            </Badge>
          </div>
        </template>
        <template #models="{ row }">{{ row.model_count }}</template>
        <template #enabled="{ row }">
          <Badge :variant="row.enabled ? 'success' : 'neutral'">
            {{ t(`visualGameAdmin.llm.providers.status.${row.enabled ? 'enabled' : 'disabled'}`) }}
          </Badge>
        </template>
        <template #actions="{ row }">
          <Button variant="ghost" size="sm" @click="openProvider(row.code)">
            {{ t('visualGameAdmin.llm.providers.table.open') }}
          </Button>
        </template>
      </Table>
    </Section>

    <ModelProviderModal
      v-model:open="modalOpen"
      :provider="null"
      @saved="catalog = $event"
    />
  </div>
</template>

<style scoped lang="scss">
.providers {
  &__identity {
    display: grid;
    gap: var(--drama-space-xs);

    code {
      color: var(--drama-color-text-muted);
      font-size: var(--drama-font-size-xs);
    }
  }

  &__capabilities {
    display: flex;
    flex-wrap: wrap;
    gap: var(--drama-space-xs);
  }
}
</style>
